import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import parse from "html-react-parser";
import React from "react";
import { Badge, Button, Card, CardGroup, Table } from "react-bootstrap";

import { IPlansRoot } from "../../config/interfaces";
import { useAppSelector } from "../../redux/Store";
import { ITRFilingSelector } from "../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../FASvgIcon";
import PriceDisplay from "../PriceDisplay";

type Props = {
  pricingPlans: IPlansRoot[];
  isProcessing?: boolean;
  handlePlanSelect: (plan: IPlansRoot) => void;
};

const PricingPlanCard: React.FC<Props> = (props: Props) => {
  const { pricingPlans, isProcessing, handlePlanSelect } = props;
  const { currentFilingPlan } = useAppSelector(ITRFilingSelector);

  return (
    <>
      <CardGroup className="plans-card-group">
        {pricingPlans?.map((plan, index) => {
          return (
            <React.Fragment key={`plan-${index}`}>
              {plan?.publishedAt && (
                <>
                  <Card
                    className={classNames(
                      "public-plan-card",
                      "shadow-none",
                      plan?.featured && "border-success"
                    )}
                    bg="white"
                  >
                    <Card.Header>
                      {plan?.featured && (
                        <Badge bg="success" pill={true} className="popular-plan">
                          Most Popular
                        </Badge>
                      )}
                      <h4 className="plan-title">{plan?.title}</h4>
                      {plan?.subTitle && <p className="plan-subtitle">{plan.subTitle}</p>}
                      {plan?.type === "self" ? (
                        <h1 className="plan-price text-primary">
                          {plan?.pricingTitle || "First Filing Free"}
                        </h1>
                      ) : (
                        <h1 className="plan-price">
                          <PriceDisplay price={plan?.pricingTitle || 0} decimalUpto={0} />
                        </h1>
                      )}
                      {plan?.pricingSubTitle && (
                        <p className="price-subtitle">{plan.pricingSubTitle}</p>
                      )}
                      <hr />
                    </Card.Header>
                    <Card.Body>
                      <p>What's included:</p>
                      {plan?.description && (
                        <div className="plan-features">{parse(plan.description)}</div>
                      )}
                      {plan?.type === "self" ? (
                        <>
                          <p>Pay less and less for more filings:</p>
                          <Table striped={true}>
                            <thead>
                              <tr>
                                <th>Filings</th>
                                <th>Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {plan.pricingRule.map((pricing, index: number) => (
                                <React.Fragment key={index}>
                                  {pricing?.minFilesCompleted === 1 &&
                                  Number(pricing?.pricePerFile || 0) === 0 ? (
                                    <tr>
                                      <td>First filing</td>
                                      <td>Free</td>
                                    </tr>
                                  ) : (
                                    <tr>
                                      <td>{pricing?.minFilesCompleted}</td>
                                      <td>
                                        <PriceDisplay
                                          price={pricing?.pricePerFile || 0}
                                          decimalUpto={0}
                                        />
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </Table>
                        </>
                      ) : null}
                    </Card.Body>
                    <Card.Footer>
                      <hr />
                      <Button
                        variant={
                          plan?.featured
                            ? "success"
                            : plan?.type === "self"
                            ? "primary"
                            : "secondary"
                        }
                        size="lg"
                        className="w-100"
                        onClick={() => {
                          if (plan.isDisabledPlan) {
                            return;
                          }
                          handlePlanSelect(plan);
                        }}
                        disabled={plan.isDisabledPlan || isProcessing}
                      >
                        {plan.uuid === currentFilingPlan?.planData.uuid &&
                        !currentFilingPlan.inactive
                          ? "Already Active"
                          : "Buy Now"}

                        {!plan.isDisabledPlan ? <FASvgIcon icon={faArrowRight} /> : null}
                      </Button>
                      <em className="plan-note">*{plan?.footerContent}</em>
                    </Card.Footer>
                  </Card>
                </>
              )}
            </React.Fragment>
          );
        })}
      </CardGroup>
    </>
  );
};

export default PricingPlanCard;
